import type { CreativeKind, CreativeSubmitRequest, CreativeVersionView } from "@townsy/contracts";

/**
 * The creative editor's rules (ADR-0009 #4 and #7, docs/08 section 5). A creative is an immutable
 * version: business name, description, monogram, two colours, an optional reviewed image and an
 * optional HTTPS website. Any change is a new version and goes back to review. These checks give
 * immediate, plain-language feedback; the server validates everything again and decides.
 */

export const TITLE_MAX = 60;
export const DESCRIPTION_MAX = 280;
export const MONOGRAM_MAX = 3;
export const URL_MAX = 2048;

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
// C0/C1 controls, zero-width and bidirectional formatting characters (spoofing), and whitespace inside a URL.
const CONTROL_RE = /[\u0000-\u001F\u007F-\u009F​-‏‪-‮⁠-⁤⁦-⁩﻿]/;
// Same lists as the server (packages/domain validateDestinationUrl), so the editor never accepts what the API refuses.
const PRIVATE_SUFFIXES = [".localhost", ".local", ".internal", ".intranet", ".lan", ".home", ".corp", ".private", ".home.arpa", ".test", ".invalid", ".example", ".onion"];
const PRIVATE_NAMES = new Set(["localhost", "localhost.localdomain", "metadata", "metadata.google.internal"]);

export type DestinationCheck =
  | { ok: true; empty: true }
  | { ok: true; empty: false; normalized: string; host: string; idn: boolean }
  | { ok: false; empty: false; message: string };

/**
 * An advertiser's website link: HTTPS only, parsed and normalised; no credentials, no port other
 * than 443, no IP literals, no single-label / private / local host names, no hidden characters.
 * Internationalised names are shown in punycode (`xn--…`) so a look-alike name is visible.
 */
export function checkDestinationUrl(input: string): DestinationCheck {
  const raw = input.trim();
  if (raw === "") return { ok: true, empty: true };
  const bad = (message: string): DestinationCheck => ({ ok: false, empty: false, message });
  if (raw.length > URL_MAX) return bad(`The link is longer than ${URL_MAX} characters.`);
  if (CONTROL_RE.test(raw) || /[\s\\]/.test(raw)) return bad("The link contains spaces, backslashes, hidden or control characters. Paste the address again.");
  const scheme = /^([a-zA-Z][a-zA-Z0-9+.-]*):/.exec(raw)?.[1]?.toLowerCase() ?? null;
  if (scheme === null) return bad("Start the link with https:// (for example https://example.com).");
  if (scheme === "http") return bad("Use a secure https:// link. Plain http:// links are not accepted.");
  if (scheme !== "https") return bad("Only https:// website links are accepted.");
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return bad("This is not a valid web address.");
  }
  if (url.protocol !== "https:") return bad("Only https:// website links are accepted.");
  if (url.username || url.password) return bad("Links with a user name or password in them are not accepted.");
  if (url.port && url.port !== "443") return bad("Links to a special port are not accepted. Use the normal https:// address.");
  let host = url.hostname.toLowerCase();
  if (host.endsWith(".")) host = host.slice(0, -1);
  if (host.startsWith("[") || host.includes(":")) return bad("Use the website's name, not an IP address.");
  if (/^[0-9.]+$/.test(host)) return bad("Use the website's name, not an IP address.");
  if (!host.includes(".")) return bad("Use a full website name such as example.com.");
  if (host.length > 253 || host.split(".").some((l) => l.length === 0 || l.length > 63 || !/^[a-z0-9-]+$/.test(l) || l.startsWith("-") || l.endsWith("-"))) {
    return bad("The website name is not a valid domain name.");
  }
  if (PRIVATE_NAMES.has(host) || PRIVATE_SUFFIXES.some((s) => host.endsWith(s))) return bad("Local, private or test addresses are not accepted. Use your public website.");
  const tld = host.slice(host.lastIndexOf(".") + 1);
  if (!/^(?:[a-z]{2,63}|xn--[a-z0-9-]{2,59})$/.test(tld)) return bad("The website name does not end in a valid domain (such as .com).");
  url.hostname = host;
  const normalized = url.href;
  if (normalized.length > URL_MAX) return bad(`The link is longer than ${URL_MAX} characters.`);
  return { ok: true, empty: false, normalized, host, idn: host.split(".").some((label) => label.startsWith("xn--")) };
}

// ---------------------------------------------------------------- colours

export function isHexColor(v: string): boolean {
  return HEX_RE.test(v);
}

/** WCAG relative luminance of a #rrggbb colour. */
export function relativeLuminance(hex: string): number {
  if (!isHexColor(hex)) return 0;
  const ch = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  const lin = ch.map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * lin[0]! + 0.7152 * lin[1]! + 0.0722 * lin[2]!;
}

export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

export const SIGN_INK = "#1b1f2a";
export const SIGN_WHITE = "#ffffff";

/** White or ink lettering, whichever reads better on the brand colour. */
export function signTextColor(brandColor: string): string {
  return contrastRatio(SIGN_WHITE, brandColor) >= contrastRatio(SIGN_INK, brandColor) ? SIGN_WHITE : SIGN_INK;
}

// ---------------------------------------------------------------- drafts

export interface CreativeDraft {
  kind: CreativeKind;
  title: string;
  description: string;
  monogram: string;
  brandColor: string;
  accentColor: string;
  /** Text-only sign (false) or with a reviewed image (true). */
  withImage: boolean;
  /** A finalized upload that the server processed successfully ("ready"). */
  uploadId: string | null;
  destinationUrl: string;
}

export type DraftField = "title" | "description" | "monogram" | "brandColor" | "accentColor" | "image" | "destinationUrl";
export type DraftErrors = Partial<Record<DraftField, string>>;

export const DEFAULT_BRAND = "#2851d9";
export const DEFAULT_ACCENT = "#f2c94c";

export function emptyDraft(kind: CreativeKind, businessName = ""): CreativeDraft {
  return {
    kind,
    title: businessName.slice(0, TITLE_MAX),
    description: "",
    monogram: suggestMonogram(businessName),
    brandColor: DEFAULT_BRAND,
    accentColor: DEFAULT_ACCENT,
    withImage: false,
    uploadId: null,
    destinationUrl: "",
  };
}

/** A new version based on an existing one (the image is uploaded again only if it changes). */
export function draftFromCreative(c: CreativeVersionView): CreativeDraft {
  return {
    kind: c.kind,
    title: c.title,
    description: c.description,
    monogram: c.monogram,
    brandColor: c.brandColor,
    accentColor: c.accentColor,
    withImage: false,
    uploadId: null,
    destinationUrl: c.destinationUrl ?? "",
  };
}

/** "Maple Bakery" -> "MB"; "Zest" -> "ZE"; letters and digits only. */
export function suggestMonogram(title: string): string {
  // Words are split on spaces and dashes; apostrophes and other marks inside a word are dropped ("Tom's Tools" -> TT).
  const words = title
    .normalize("NFKC")
    .split(/[\s\-–—_/]+/u)
    .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ""))
    .filter(Boolean);
  if (words.length === 0) return "";
  if (words.length === 1) return Array.from(words[0]!).slice(0, 2).join("").toUpperCase();
  return words
    .slice(0, 2)
    .map((w) => Array.from(w)[0]!)
    .join("")
    .toUpperCase();
}

const graphemes = (s: string) => Array.from(s).length;

export function validateDraft(d: CreativeDraft, upload: { ready: boolean; busy: boolean }): DraftErrors {
  const e: DraftErrors = {};
  const title = d.title.trim();
  if (!title) e.title = "Enter the name shown on the sign.";
  else if (graphemes(title) > TITLE_MAX) e.title = `Use at most ${TITLE_MAX} characters.`;
  else if (CONTROL_RE.test(title)) e.title = "Remove hidden or control characters.";
  if (graphemes(d.description) > DESCRIPTION_MAX) e.description = `Use at most ${DESCRIPTION_MAX} characters.`;
  else if (CONTROL_RE.test(d.description.replace(/[\n\t]/g, " "))) e.description = "Remove hidden or control characters.";
  const mono = d.monogram.trim();
  if (!mono) e.monogram = "Enter 1 to 3 letters or digits (shown while a logo loads, and on text-only signs).";
  else if (graphemes(mono) > MONOGRAM_MAX) e.monogram = `Use at most ${MONOGRAM_MAX} characters.`;
  else if (!/^[\p{L}\p{N}&+]+$/u.test(mono)) e.monogram = "Use letters, digits, & or + only.";
  if (!isHexColor(d.brandColor)) e.brandColor = "Enter a colour like #2851d9.";
  if (!isHexColor(d.accentColor)) e.accentColor = "Enter a colour like #f2c94c.";
  if (d.withImage) {
    if (upload.busy) e.image = "Wait until the image has been checked.";
    else if (!d.uploadId || !upload.ready) e.image = d.kind === "billboard" ? "Upload the billboard image, or switch to a text-only design." : "Upload the logo, or switch to a text-only sign.";
  }
  const dest = checkDestinationUrl(d.destinationUrl);
  if (!dest.ok) e.destinationUrl = dest.message;
  return e;
}

export function hasErrors(e: DraftErrors): boolean {
  return Object.keys(e).length > 0;
}

/** The request body for `POST /creatives` (null when the draft is not valid). */
export function toSubmitRequest(d: CreativeDraft, advertiserId: string, upload: { ready: boolean; busy: boolean }): CreativeSubmitRequest | null {
  if (hasErrors(validateDraft(d, upload))) return null;
  const dest = checkDestinationUrl(d.destinationUrl);
  return {
    advertiserId,
    kind: d.kind,
    title: d.title.trim(),
    description: d.description.trim(),
    monogram: d.monogram.trim().toUpperCase(),
    brandColor: d.brandColor.toLowerCase(),
    accentColor: d.accentColor.toLowerCase(),
    uploadId: d.withImage ? d.uploadId : null,
    destinationUrl: dest.ok && !dest.empty ? dest.normalized : null,
  };
}

// ---------------------------------------------------------------- live sign preview

export interface SignPreviewModel {
  kind: CreativeKind;
  title: string;
  monogram: string;
  description: string;
  background: string;
  accent: string;
  text: string;
  /** Same-origin image (approved derivative or the owner's private preview), or null for a text sign. */
  imageUrl: string | null;
  mode: "image" | "text";
  host: string | null;
  /** Accessible description of the preview. */
  label: string;
  /** Brand vs accent colours are hard to tell apart. */
  lowAccentContrast: boolean;
}

export function signPreview(
  d: Pick<CreativeDraft, "kind" | "title" | "monogram" | "description" | "brandColor" | "accentColor" | "destinationUrl">,
  imageUrl: string | null,
): SignPreviewModel {
  const background = isHexColor(d.brandColor) ? d.brandColor : DEFAULT_BRAND;
  const accent = isHexColor(d.accentColor) ? d.accentColor : DEFAULT_ACCENT;
  const title = d.title.trim() || "Your business name";
  const monogram = (d.monogram.trim() || suggestMonogram(title) || "?").toUpperCase();
  const dest = checkDestinationUrl(d.destinationUrl);
  const host = dest.ok && !dest.empty ? dest.host : null;
  const mode = imageUrl ? "image" : "text";
  const what = d.kind === "billboard" ? "Billboard" : "Store sign";
  return {
    kind: d.kind,
    title,
    monogram,
    description: d.description.trim(),
    background,
    accent,
    text: signTextColor(background),
    imageUrl,
    mode,
    host,
    label: `${what} preview: ${mode === "image" ? "your image" : `the monogram ${monogram}`} with "${title}" on ${background}${host ? `, links to ${host}` : ", no website link"}.`,
    lowAccentContrast: contrastRatio(background, accent) < 1.5,
  };
}

/** The creative as it was submitted (library cards). */
export function previewOfCreative(c: CreativeVersionView, imageUrl: string | null): SignPreviewModel {
  return signPreview({ ...c, destinationUrl: c.destinationUrl ?? "" }, imageUrl);
}
